import { useQuery, useQueryClient } from "@tanstack/react-query";
import MyAgreements from "@/components/MyAgreements";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LogOut, ArrowRight, CheckCircle2, Calendar, Users, TrendingUp, Sparkles, FileText, Download, Flame, Compass, Video, X, Gift, Printer, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { payFor } from "@/lib/checkout";
import { toast } from "sonner";
import EnhancerDashboard from "@/pages/EnhancerDashboard";
import CouncilDashboard from "@/pages/CouncilDashboard";
import LocationManagerDashboard from "@/pages/LocationManagerDashboard";
import ActionBanner from "@/components/ActionBanner";
import ReviewPrompts from "@/components/ReviewPrompts";
import SessionsPanel from "@/components/SessionsPanel";
import SavedCardsPanel from "@/components/SavedCardsPanel";
import ManageSubscriptionPanel from "@/components/ManageSubscriptionPanel";
import BookingModal from "@/components/BookingModal";
import PackageSchedulerModal from "@/components/PackageSchedulerModal";
import ShowcasePanel from "@/components/showcase/ShowcasePanel";
import MemberHistoryPanel from "@/components/MemberHistoryPanel";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";
import MemberHome, { MemberFeaturesButton } from "@/components/member/MemberHome";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DayPicker } from "@/components/ui/calendar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const ROLE_LABELS = {
  discovery: "Discovery Member",
  community: "Community Member",
  enhancer: "Enhancer",
  council: "Council Member",
  vendor: "Vendor",
  speaker: "Speaker",
  musician: "Musician",
  staff: "Staff",
  admin: "Admin",
};

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="eyw-card p-6">
      <div className="flex items-center justify-between">
        <span className="text-3xl font-serif text-[#144F75]">{value}</span>
        {Icon && <Icon size={20} className="text-[#D98E73]" />}
      </div>
      <p className="text-sm text-[#4A5D6B] mt-1">{label}</p>
    </div>
  );
}

const hoursUntil = (b) => (new Date(`${b.date}T${b.start_time || "00:00"}`) - new Date()) / 36e5;

function CancelBooking({ booking, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!["pending", "confirmed", "reschedule_proposed"].includes(booking.status) || hoursUntil(booking) < 24) return null;
  const open = async () => {
    try { setPreview((await api.get(`/me/bookings/${booking.id}/cancel-preview`)).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/me/bookings/${booking.id}/cancel`); toast.success("Your booking is cancelled."); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setPreview(null); }
  };
  return (
    <>
      <button onClick={open} data-testid={`my-booking-cancel-${booking.id}`} className="text-xs text-red-500 hover:text-red-600 ml-3">Cancel booking</button>
      <AlertDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <AlertDialogContent data-testid={`cancel-dialog-${booking.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel {booking.service_title}?</AlertDialogTitle>
            <AlertDialogDescription>
              {fmtDate(booking.date)} at {fmtTimeRange(booking.start_time)}.{" "}
              {preview?.refund_due ? `${money(preview.refund_due)} will be refunded to you.` : preview?.forfeited ? `Under the cancellation policy your ${money(preview.forfeited)} deposit is kept.` : "No payment has been taken."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`cancel-dialog-keep-${booking.id}`}>Keep booking</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} disabled={busy} data-testid={`cancel-dialog-confirm-${booking.id}`} className="bg-red-600 hover:bg-red-700">Cancel booking</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SelfReschedule({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const canReschedule = ["pending", "confirmed"].includes(booking.status) && booking.service_id;
  const tooLate = hoursUntil(booking) < 24;
  const [calOpen, setCalOpen] = useState(false);
  const { data: slotData } = useQuery({
    queryKey: ["self-resched-slots", booking.id, date],
    queryFn: () => api.get(`/services/${booking.service_id}/open-slots`, { params: { date, location_id: booking.location_id, approval_id: booking.approval_id || "", enhancer_id: booking.enhancer_id || "" } }).then((r) => r.data),
    enabled: open && !!date,
  });
  const slots = slotData?.slots || [];
  const findNext = async () => {
    try {
      const { data } = await api.get(`/services/${booking.service_id}/next-available`, { params: { location_id: booking.location_id, enhancer_id: booking.enhancer_id || "", approval_id: booking.approval_id || "" } });
      if (data.date) { setDate(data.date); toast.success(`Next opening: ${data.date}`); }
      else toast.error("No openings in the next 30 days with your provider.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pick = async (t) => {
    setBusy(true);
    try {
      await api.post(`/me/bookings/${booking.id}/reschedule`, { date, start_time: t });
      toast.success("Rescheduled. You'll get a confirmation shortly.");
      setOpen(false); onDone();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!canReschedule) return null;
  if (tooLate) return <p className="mt-2 text-xs text-[#8FA89B]" data-testid={`resched-too-late-${booking.id}`}>Less than 24 hours away, so changes need a message to your enhancer.</p>;
  const rr = booking.reschedule_request;
  if (rr?.status === "pending") {
    return (
      <p className="mt-2 text-xs text-[#B5794E] bg-[#E0A458]/10 border border-[#E0A458]/40 rounded-lg px-2.5 py-1.5" data-testid={`resched-pending-${booking.id}`}>
        Requested {fmtDate(rr.date)} at {fmtTimeRange(rr.start_time)} — awaiting the center's approval.
      </p>
    );
  }
  return (
    <div className="mt-2" data-testid={`self-reschedule-${booking.id}`}>
      {rr?.status === "declined" && (
        <p className="text-xs text-[#B5794E] mb-1.5" data-testid={`resched-declined-${booking.id}`}>
          The center couldn't move you to {fmtDate(rr.date)} at {fmtTimeRange(rr.start_time)}{rr.decline_note ? ` — “${rr.decline_note}”` : ""}. Propose another time below.
        </p>
      )}
      {!open ? (
        <span><button onClick={() => setOpen(true)} data-testid={`self-reschedule-open-${booking.id}`} className="text-xs text-[#144F75] font-medium hover:underline">Reschedule</button><CancelBooking booking={booking} onDone={onDone} /></span>
      ) : (
        <div className="rounded-xl bg-[#F9F7F3] border border-[#E5E0D8] p-3">
          <p className="text-xs text-[#4A5D6B] mb-2">Pick a new open time with <b>{booking.enhancer_name || "your provider"}</b>{booking.location ? ` at ${booking.location}` : ""}:</p>
          <div className="flex items-center gap-2 mb-2">
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button data-testid={`self-reschedule-date-${booking.id}`} className="rounded-lg border border-[#E5E0D8] bg-white px-2.5 py-1.5 text-sm inline-flex items-center gap-1.5"><Calendar size={14} /> {date ? fmtDate(date) : "Choose a date"}</button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <DayPicker mode="single" selected={date ? new Date(`${date}T12:00`) : undefined} disabled={{ before: new Date(Date.now() + 864e5) }}
                  onSelect={(d) => d && (setCalOpen(false), setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`))} data-testid={`self-reschedule-calendar-${booking.id}`} />
              </PopoverContent>
            </Popover>
            <button onClick={findNext} data-testid={`self-reschedule-next-${booking.id}`} className="text-xs text-[#144F75] hover:underline">Next available</button>
            <button onClick={() => setOpen(false)} className="text-xs text-[#8FA89B] hover:underline ml-auto">Close</button>
          </div>
          {date && (slots.length === 0 ? <p className="text-xs text-[#B5794E]">No open times that day — try another date or Next available.</p> :
            <div className="flex flex-wrap gap-1.5">
              {slots.map((t) => <button key={t} disabled={busy} onClick={() => pick(t)} data-testid={`self-reschedule-slot-${booking.id}`} className="text-xs px-2.5 py-1 rounded-md bg-white border border-[#E5E0D8] hover:bg-[#144F75] hover:text-white transition-colors">{t}</button>)}
            </div>)}
        </div>
      )}
    </div>
  );
}

// Online room closes 60 min after the session ends (business time, ET).
const sessionEnded = (b) => {
  if (!b.date || !b.start_time) return false;
  const end = new Date(`${b.date}T${b.start_time}:00Z`).getTime() + ((b.duration_min || 60) + 60) * 60000;
  const nowEt = new Date(new Date().toLocaleString("sv-SE", { timeZone: "America/New_York" }).replace(" ", "T") + "Z").getTime();
  return nowEt > end;
};


export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/me/dashboard").then((r) => r.data),
    enabled: !!user,
  });
  const { data: bookings = [] } = useQuery({
    queryKey: ["my-bookings"],
    queryFn: () => api.get("/me/bookings").then((r) => r.data),
    enabled: !!user,
  });
  const respondReschedule = async (id, action) => {
    try {
      await api.put(`/me/appointments/${id}/${action}-reschedule`);
      toast.success(action === "accept" ? "New time accepted — you're all set." : "New time declined.");
      qc.invalidateQueries({ queryKey: ["my-bookings"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const { data: registrations = [] } = useQuery({
    queryKey: ["my-registrations"],
    queryFn: () => api.get("/me/registrations").then((r) => r.data),
    enabled: !!user,
  });
  const { data: signatures = [] } = useQuery({
    queryKey: ["my-signatures"],
    queryFn: () => api.get("/me/signatures").then((r) => r.data),
    enabled: !!user,
  });
  const { data: creditsData } = useQuery({
    queryKey: ["my-package-credits"],
    queryFn: () => api.get("/me/package-credits").then((r) => r.data),
    enabled: !!user,
  });
  const packageCredits = creditsData?.credits || [];
  const queryClient = useQueryClient();
  const [pkgService, setPkgService] = useState(null);   // service to book with a package credit
  const [chooserCredit, setChooserCredit] = useState(null); // credit whose eligible services we're choosing from
  const [schedCredit, setSchedCredit] = useState(null); // credit being multi-scheduled
  const openBook = (svc) => { setChooserCredit(null); setPkgService({ id: svc.id, title: svc.title, price: Number(svc.price || 0), duration_min: svc.duration_min || 60, member_discount_pct: 0, service_locations: [] }); };
  const startBooking = (c) => {
    const svcs = c.eligible_services || [];
    if (svcs.length === 1) openBook(svcs[0]);
    else if (svcs.length > 1) setChooserCredit(c);
    else nav("/wellness");
  };
  const { data: orders = [] } = useQuery({
    queryKey: ["my-orders"],
    queryFn: () => api.get("/me/orders").then((r) => r.data),
    enabled: !!user,
  });
  const { data: giftsSent = [] } = useQuery({
    queryKey: ["my-gift-purchases"],
    queryFn: () => api.get("/me/gift-purchases").then((r) => r.data),
    enabled: !!user,
  });
  const { data: coach } = useQuery({
    queryKey: ["dashboard-coach"],
    queryFn: () => api.get("/me/gauge/coach").then((r) => r.data),
    enabled: !!user,
  });

  const downloadPdf = async (sig) => {
    const res = await api.get(`/me/signatures/${sig.id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sig.agreement_title || "agreement"}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "staff")) nav("/admin", { replace: true });
  }, [user, nav]);

  if (user && user.role === "enhancer") return <EnhancerDashboard />;
  if (user && user.role === "vendor") return <EnhancerDashboard />;
  if (user && (user.role === "council")) return <CouncilDashboard />;
  if (user && user.role === "location_manager") return <LocationManagerDashboard />;

  if (isLoading || !data) return <div className="min-h-[60vh] grid place-items-center text-[#4A5D6B]">Loading your dashboard…</div>;

  const eco = data.ecosystem;
  const stats = data.enhancer_stats;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <p className="eyw-label mb-1">{ROLE_LABELS[data.role] || "Member"} Dashboard</p>
          <h1 className="font-serif text-4xl text-[#1A242B]">Welcome, {data.name || "friend"}.</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <MemberFeaturesButton />
          <Link to="/settings/profile" data-testid="dashboard-profile-link" className="eyw-btn-ghost !py-2 text-sm"><Sparkles size={15} /> Profile & Privacy</Link>
          <button onClick={logout} data-testid="dashboard-logout" className="eyw-btn-ghost !py-2 text-sm">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </div>

      <MemberHome />

      {data.birthday_week && (
        <div className="rounded-2xl bg-gradient-to-r from-[#D98E73]/15 to-[#E0A458]/10 border border-[#D98E73]/30 px-5 py-4 mb-6 text-sm text-[#8A5A3B]" data-testid="dashboard-birthday-banner">
          🎂 <span className="font-medium">Happy birthday week, {(data.name || "friend").split(" ")[0]}!</span> We hope it's a beautiful one — mention it at check-in, your center may have a little treat waiting for you.
        </div>
      )}

      <ActionBanner />
      <ReviewPrompts />

      {data.role === "community" && (
        <div className="mb-8"><ShowcasePanel /></div>
      )}

      {/* Become a Vendor — for members who make/sell (musicians, crafters, homesteaders, artisans) */}
      {["discovery", "community"].includes(data.role) && (
        <div className="relative overflow-hidden eyw-card p-6 md:p-7 mb-8 flex flex-wrap items-center gap-5" data-testid="dashboard-vendor-cta">
          <div className="shrink-0 grid place-items-center w-12 h-12 rounded-2xl bg-[#9E5C7F]/10 text-[#9E5C7F]"><Store size={22} /></div>
          <div className="flex-1 min-w-[240px]">
            <h2 className="font-serif text-2xl text-[#1A242B]">Make something? Sell it here.</h2>
            <p className="text-[#4A5D6B] mt-1 text-sm">Musicians, crafters, homesteaders and artisans — apply to become a Vendor and list your products in our marketplace with your own seller dashboard and payouts.</p>
          </div>
          <Link to="/vendor/apply" data-testid="dashboard-vendor-apply" className="eyw-btn !bg-[#9E5C7F] hover:!bg-[#874d6c] whitespace-nowrap">Become a Vendor <ArrowRight size={16} /></Link>
        </div>
      )}

      {/* Well-being companion */}
      <div className="relative overflow-hidden rounded-3xl bg-[#144F75] p-6 md:p-8 mb-8 flex flex-wrap items-center gap-6" data-testid="dashboard-companion">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 15% 30%, #fff 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
        <div className="relative flex-1 min-w-[240px]">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D98E73] mb-2 flex items-center gap-1.5"><Compass size={13} /> Your Daily Practice</p>
          <h2 className="font-serif text-2xl md:text-3xl text-white leading-snug">
            {coach?.checked_in_today ? "You've reflected today. Beautiful." : "A quiet moment for you today."}
          </h2>
          {coach?.prompt && <p className="text-white/80 mt-2 max-w-xl">{coach.prompt.text}</p>}
        </div>
        <div className="relative flex items-center gap-5">
          <div className="text-center">
            <div className="font-serif text-3xl text-white flex items-center justify-center gap-1.5"><Flame size={20} className="text-[#D98E73]" /> {coach?.stats?.streak ?? 0}</div>
            <p className="text-xs text-white/60 mt-0.5">day streak</p>
          </div>
          <Link to="/coach" data-testid="dashboard-coach-btn" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60] whitespace-nowrap">Open Daily Coach <ArrowRight size={16} /></Link>
        </div>
      </div>

      {/* Council ecosystem overview */}
      {eco && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10" data-testid="council-overview">
          <Stat label="Total Members" value={eco.members} icon={Users} />
          <Stat label="Discovery" value={eco.discovery} icon={Sparkles} />
          <Stat label="Community" value={eco.community} icon={Users} />
          <Stat label="Enhancers" value={eco.enhancers} icon={TrendingUp} />
          <Stat label="Pending Apps" value={eco.pending_enhancer_apps} icon={CheckCircle2} />
          <Stat label="Upcoming Events" value={eco.upcoming_events} icon={Calendar} />
        </div>
      )}

      {/* Enhancer stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10" data-testid="enhancer-overview">
          <Stat label="Services" value={stats.services} />
          <Stat label="Bookings" value={stats.bookings} />
          <Stat label="Pending Earnings" value={`${money(stats.pending_earnings)}`} />
        </div>
      )}

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
        {/* Next steps / Bridge the Gaps */}
        <div className="eyw-card p-8">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Your next steps</h2>
          <p className="text-[#4A5D6B] text-sm mb-6">We help bridge the gaps so you can practice your enhancements.</p>
          {data.gaps && data.gaps.length > 0 ? (
            <ul className="space-y-3" data-testid="dashboard-gaps">
              {data.gaps.map((g, i) => (
                <li key={i} className="flex items-center gap-3 rounded-xl border border-[#E5E0D8] px-4 py-3">
                  <span className="grid place-items-center h-7 w-7 rounded-full bg-[#144F75]/10 text-[#144F75] text-sm shrink-0">{i + 1}</span>
                  <span className="text-sm text-[#1A242B]">{g}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[#4A5D6B] text-sm">You're all caught up.</p>
          )}
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/coach" className="eyw-btn !py-2 text-sm" data-testid="dashboard-gauge-btn">Daily Check-in</Link>
            <Link to="/gauge/app" className="eyw-btn-ghost !py-2 text-sm">Explore GAUGE</Link>
            <Link to="/wellness" className="eyw-btn-ghost !py-2 text-sm">Book a session</Link>
            <Link to="/showcase" className="eyw-btn-ghost !py-2 text-sm" data-testid="dashboard-showcase-link">Community Showcase</Link>
            {data.role === "discovery" && <Link to="/register?role=community" className="eyw-btn !py-2 text-sm">Upgrade to Community</Link>}
          </div>
        </div>

        {/* Upcoming events */}
        <div className="eyw-card p-8">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-6">Upcoming in the ecosystem</h2>
          {data.upcoming_events && data.upcoming_events.length > 0 ? (
            <ul className="space-y-4" data-testid="dashboard-events">
              {data.upcoming_events.map((e) => (
                <li key={e.id} className="border-b border-[#E5E0D8] pb-4 last:border-0">
                  <div className="text-sm font-semibold text-[#1A242B]">{e.title}</div>
                  <div className="text-xs text-[#4A5D6B] mt-0.5">{fmtDate(e.date)} · {fmtTimeRange(e.start_time)} · {e.location}</div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[#4A5D6B] text-sm">No upcoming events yet.</p>
          )}
          <Link to="/events" className="inline-flex items-center gap-1 text-[#144F75] text-sm font-medium mt-6 hover:gap-2 transition-all">
            Full schedule <ArrowRight size={15} />
          </Link>
        </div>
      </div>

      {packageCredits.length === 0 && (
        <Link to="/programs" className="eyw-card p-6 mt-8 flex items-center justify-between hover:-translate-y-0.5 transition-transform" data-testid="dashboard-my-programs-link">
          <span><span className="font-serif text-2xl text-[#1A242B] block">My Programs</span><span className="text-sm text-[#4A5D6B]">Your groups, upcoming sessions and broadcasts in one place</span></span>
          <ArrowRight size={18} className="text-[#144F75]" />
        </Link>
      )}

      {/* My package credits */}
      {packageCredits.length > 0 && (
        <div className="eyw-card p-4 sm:p-8 mt-8" data-testid="my-packages">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-serif text-2xl text-[#1A242B]">My programs & packages</h2>
            <div className="flex flex-wrap gap-2">
              <Link to="/programs" className="eyw-btn-ghost !py-2 text-sm" data-testid="dashboard-my-programs-link">My Programs <ArrowRight size={15} /></Link>
              <Link to="/wellness" className="eyw-btn-ghost !py-2 text-sm">Schedule a session <ArrowRight size={15} /></Link>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packageCredits.map((c) => (
              <div key={c.id} className="rounded-2xl border border-[#E5E0D8] p-5 bg-[#F9F7F3]" data-testid={`package-credit-${c.id}`}>
                <div className="flex items-baseline justify-between">
                  <h3 className="font-serif text-lg text-[#1A242B]">{c.package_name}</h3>
                  <span className="text-2xl font-serif text-[#144F75]" data-testid={`credit-remaining-${c.id}`}>{c.sessions_remaining}</span>
                </div>
                <p className="text-xs text-[#8FA89B] mb-3" data-testid={`credit-unscheduled-${c.id}`}>of {c.sessions_total} sessions remaining — unscheduled{c.expires_at ? ` · expires ${String(c.expires_at).slice(0, 10)}` : ""}</p>
                {c.eligible_services?.length > 0 && (
                  <p className="text-xs text-[#4A5D6B] leading-relaxed">Use for: {c.eligible_services.slice(0, 6).map((s) => s.title).join(", ")}</p>
                )}
                <button onClick={() => setSchedCredit(c)} data-testid={`package-schedule-${c.id}`} className="eyw-btn !py-2 !px-4 text-sm mt-4 w-full justify-center">
                  <Calendar size={15} /> Schedule sessions
                </button>
                <button onClick={() => startBooking(c)} data-testid={`package-book-${c.id}`} className="text-xs text-[#144F75] hover:underline mt-2 w-full text-center">
                  More booking options (choose provider)
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Choose which eligible service to redeem a package session for */}
      {chooserCredit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setChooserCredit(null)} data-testid="package-service-chooser">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-serif text-xl text-[#1A242B]">Book with your {chooserCredit.package_name}</h3>
              <button onClick={() => setChooserCredit(null)} data-testid="chooser-close" className="text-[#8FA89B] hover:text-[#1A242B]"><X size={20} /></button>
            </div>
            <p className="text-sm text-[#4A5D6B] mb-4">Choose the session you'd like to book. {chooserCredit.sessions_remaining} remaining.</p>
            <div className="space-y-2">
              {(chooserCredit.eligible_services || []).map((s) => (
                <button key={s.id} onClick={() => openBook(s)} data-testid={`chooser-service-${s.id}`}
                  className="w-full flex items-center justify-between rounded-xl border border-[#E5E0D8] px-4 py-3 text-left hover:border-[#144F75] hover:bg-[#144F75]/[0.04] transition-colors">
                  <span className="text-sm font-medium text-[#1A242B]">{s.title}</span>
                  <span className="text-xs text-[#8FA89B]">{s.duration_min || 60} min</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {pkgService && <BookingModal service={pkgService} onClose={() => { setPkgService(null); queryClient.invalidateQueries({ queryKey: ["my-package-credits"] }); queryClient.invalidateQueries({ queryKey: ["my-bookings"] }); }} />}
      {schedCredit && <PackageSchedulerModal credit={schedCredit} onClose={() => setSchedCredit(null)} />}

      <MemberHistoryPanel />

      {/* My bookings & registrations */}
      {(bookings.length > 0 || registrations.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-8 mt-8" data-testid="my-activity">
          <div className="eyw-card p-8">
            <h2 className="font-serif text-2xl text-[#1A242B] mb-5">My bookings</h2>
            {bookings.length === 0 ? <p className="text-[#4A5D6B] text-sm">No bookings yet.</p> : (
              <ul className="space-y-3">
                {bookings.map((b) => (
                  <li key={b.id} className="border-b border-[#E5E0D8] pb-3 last:border-0" data-testid={`my-booking-${b.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-[#1A242B]">{b.service_title}</div>
                        <div className="text-xs text-[#4A5D6B]">{[fmtDate(b.date), fmtTimeRange(b.start_time), b.location].filter(Boolean).join(" · ")}</div>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full ${b.status === "confirmed" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : b.status === "reschedule_proposed" ? "bg-[#144F75]/10 text-[#144F75]" : b.status === "cancelled" ? "bg-red-100 text-red-500" : "bg-[#D98E73]/15 text-[#B5794E]"}`} data-testid={`my-booking-status-${b.id}`}>
                        {b.approval_state === "pending_approval" ? "pending approval" : b.payment_status === "unpaid" ? "unpaid" : b.status === "pending" ? "requested" : b.status === "reschedule_proposed" ? "new time proposed" : b.status}
                      </span>
                    </div>
                    {b.agreements_status === "awaiting" && b.status !== "cancelled" && (
                      <div className="mt-2 rounded-xl bg-[#D98E73]/10 border border-[#D98E73]/30 p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`booking-awaiting-${b.id}`}>
                        <p className="text-xs text-[#B5794E]">Held — sign your agreements to fully confirm this booking.</p>
                        <Link to={`/booking-agreements/${b.agreement_token}`} data-testid={`booking-sign-agreements-${b.id}`} className="eyw-btn !py-1.5 !px-3 text-xs">Sign now</Link>
                      </div>
                    )}
                    {b.approval_state === "declined" && b.decline_reason && (
                      <div className="mt-2 rounded-xl bg-red-50 border border-red-100 p-3" data-testid={`booking-declined-${b.id}`}>
                        <p className="text-xs text-red-600">This appointment was declined by the center. Reason: {b.decline_reason}</p>
                      </div>
                    )}
                    {b.zoom_join_url && b.status !== "cancelled" && (
                      <a href={b.zoom_join_url} target="_blank" rel="noreferrer" data-testid={`booking-zoom-join-${b.id}`}
                        className="mt-2.5 inline-flex items-center gap-2 rounded-full bg-[#5C8A72] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4d7561] transition-colors">
                        <Video size={15} /> Join Zoom session
                      </a>
                    )}
                    {!b.zoom_join_url && (b.video_room_name || b.location_id === "virtual") && b.status !== "cancelled" && !sessionEnded(b) && (
                      <Link to={`/session/${b.id}`} data-testid={`booking-video-join-${b.id}`}
                        className="mt-2.5 inline-flex items-center gap-2 rounded-full bg-[#5C8A72] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4d7561] transition-colors">
                        <Video size={15} /> Join online session
                      </Link>
                    )}
                    {b.status === "reschedule_proposed" && b.proposed_date && (
                      <div className="mt-2 rounded-xl bg-[#144F75]/[0.04] border border-[#144F75]/10 p-3" data-testid={`reschedule-proposal-${b.id}`}>
                        <p className="text-xs text-[#1A242B]">New time proposed: <b>{b.proposed_date} at {b.proposed_time}</b></p>
                        {b.reschedule_note && <p className="text-xs text-[#4A5D6B] mt-1">"{b.reschedule_note}"</p>}
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => respondReschedule(b.id, "accept")} data-testid={`accept-reschedule-${b.id}`} className="eyw-btn !py-1.5 !px-3 text-xs">Accept new time</button>
                          <button onClick={() => respondReschedule(b.id, "decline")} data-testid={`decline-reschedule-${b.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs">Decline</button>
                        </div>
                      </div>
                    )}
                    <SelfReschedule booking={b} onDone={() => { qc.invalidateQueries({ queryKey: ["my-bookings"] }); qc.invalidateQueries({ queryKey: ["member-home"] }); }} />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="eyw-card p-8">
            <h2 className="font-serif text-2xl text-[#1A242B] mb-5">My event registrations</h2>
            {registrations.length === 0 ? <p className="text-[#4A5D6B] text-sm">No registrations yet.</p> : (
              <ul className="space-y-3">
                {registrations.filter((r) => r.status !== "cancelled").map((r) => (
                  <li key={r.id} className="flex items-center justify-between border-b border-[#E5E0D8] pb-3 last:border-0" data-testid={`my-reg-${r.id}`}>
                    <div>
                      <div className="text-sm font-semibold text-[#1A242B]">{r.event_title}</div>
                      <div className="text-xs text-[#4A5D6B]">
                        {fmtDate(r.date)}{r.tier && ` · ${r.tier}`}
                        {r.meal_option && ` · ${r.meal_option}`}{r.lodging_option && ` · ${r.lodging_option}`}
                        {r.price > 0 && ` · ${money(r.price)}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {r.status === "waitlisted" && <span className="text-xs px-2.5 py-1 rounded-full bg-[#E0A458]/15 text-[#B5794E]" data-testid={`reg-waitlisted-${r.id}`}>waitlisted</span>}
                      {r.status !== "waitlisted" && (
                        <span className={`text-xs px-2.5 py-1 rounded-full ${r.payment_status === "paid" ? "bg-[#144F75]/10 text-[#144F75]" : r.payment_status === "free" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>
                          {r.payment_status === "unpaid" ? "unpaid" : r.status}
                        </span>
                      )}
                      {r.status !== "waitlisted" && r.status !== "cancelled" && (
                        <a href={`/ticket/${r.id}`} target="_blank" rel="noreferrer" data-testid={`reg-ticket-${r.id}`} className="text-xs text-[#144F75] hover:underline">View ticket</a>
                      )}
                      {r.status === "pending" && r.payment_status === "unpaid" && r.price > 0 && (
                        <button onClick={() => payFor("registration", r.id)} data-testid={`reg-pay-${r.id}`} className="eyw-btn !py-1 !px-3 text-xs">Pay now</button>
                      )}
                      <button onClick={async () => { if (window.confirm("Cancel this registration?")) { await api.post(`/registrations/${r.id}/cancel`); toast.success("Registration cancelled"); qc.invalidateQueries({ queryKey: ["my-registrations"] }); } }} data-testid={`reg-cancel-${r.id}`} className="text-xs text-red-500 hover:text-red-600">Cancel</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* My orders */}
      {orders.length > 0 && (
        <div className="eyw-card p-8 mt-8" data-testid="my-orders">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl text-[#1A242B]">My orders</h2>
            <Link to="/orders" className="text-sm text-[#144F75] hover:underline" data-testid="view-order-history">View full history →</Link>
          </div>
          <ul className="space-y-3">
            {orders.map((o) => (
              <li key={o.id} className="border-b border-[#E5E0D8] pb-3 last:border-0" data-testid={`my-order-${o.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-[#1A242B]">{o.items.map((i) => `${i.title} ×${i.qty}`).join(", ")}</div>
                    <div className="text-xs text-[#4A5D6B]">{o.created_at?.slice(0, 10)} · {money(o.total)}{o.requires_shipping ? " · ships to you" : ""}</div>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${o.payment_status === "paid" ? "bg-[#144F75]/10 text-[#144F75]" : o.payment_status === "free" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>
                    {o.payment_status === "unpaid" ? "unpaid" : o.status}
                  </span>
                </div>
                {o.items.filter((i) => i.tracking_number).map((i, idx) => (
                  <div key={idx} className="text-xs text-[#5C8A72] mt-1" data-testid={`order-tracking-${o.id}-${idx}`}>
                    ✓ {i.title} shipped · {(i.carrier || "").toUpperCase()} {i.tracking_number}
                  </div>
                ))}
                {o.items.filter((i) => i.refund_status && i.refund_status !== "none").map((i, idx) => (
                  <div key={`r${idx}`} className="text-xs text-[#B5794E] mt-1">
                    {i.title} · {i.refund_status === "refunded" ? "refunded" : `partially refunded (${money((i.refunded_amount || 0))})`}
                  </div>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* My Agreements */}
      <MyAgreements />

      {giftsSent.length > 0 && (
        <div className="eyw-card p-8 mt-8" data-testid="gifts-sent-panel">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-1 flex items-center gap-2"><Gift size={20} className="text-[#D98E73]" /> Gifts you've sent</h2>
          <p className="text-sm text-[#4A5D6B] mb-5">Print a keepsake certificate to hand to your recipient in person.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {giftsSent.map((g, i) => (
              <div key={i} className="border border-[#E5E0D8] rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`gift-sent-${i}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-[#1A242B]">{g.package_name}</p>
                  <p className="text-sm text-[#4A5D6B]">{g.sessions} session{g.sessions === 1 ? "" : "s"} · for {g.recipient_name || g.recipient_email}</p>
                </div>
                <a href={`/gift-certificate?token=${g.certificate_token}`} target="_blank" rel="noreferrer" data-testid={`gift-cert-${i}`} className="eyw-btn-ghost !py-2 !px-3 text-sm shrink-0"><Printer size={15} /> Certificate</a>
              </div>
            ))}
          </div>
        </div>
      )}

      <ManageSubscriptionPanel />

      <SavedCardsPanel />

      <SessionsPanel />
    </div>
  );
}